import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, RotateCcw } from 'lucide-react';
import { API } from '@/utils/api';

// Compute current FY string ("2026-27") from the user's local date.
const currentFY = () => {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() + 1 >= 4 ? y : y - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
};

const fyOptions = (count = 6) => {
  const cur = currentFY();
  const startYear = parseInt(cur.slice(0, 4), 10);
  const out = [];
  for (let i = 0; i < count; i++) {
    const sy = startYear - i;
    out.push(`${sy}-${String((sy + 1) % 100).padStart(2, '0')}`);
  }
  return out;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

/**
 * Goods Return / Credit Note report dialog — pick a period (day / month / FY)
 * and download the goods-return statement as a PDF. Used for a single customer
 * (customer_code) or for the whole filtered view.
 *
 * Props:
 *   open, onOpenChange — standard dialog props
 *   customer: { customer_code?, name? } — optional; when omitted, exports the
 *             whole division/filter scope
 *   division: string | undefined
 *   defaultPeriod: 'day' | 'month' | 'fy'  (default 'month')
 *   defaultPeriodValue: string (matching period type)
 */
export default function GoodsReturnReportDialog({
  open,
  onOpenChange,
  customer,
  division,
  defaultPeriod = 'month',
  defaultPeriodValue,
}) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [dayValue, setDayValue] = useState(defaultPeriod === 'day' && defaultPeriodValue ? defaultPeriodValue : todayISO());
  const [monthValue, setMonthValue] = useState(defaultPeriod === 'month' && defaultPeriodValue ? defaultPeriodValue : thisMonth());
  const [fyValue, setFyValue] = useState(defaultPeriod === 'fy' && defaultPeriodValue ? defaultPeriodValue : currentFY());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPeriod(defaultPeriod);
    if (defaultPeriod === 'day') setDayValue(defaultPeriodValue || todayISO());
    if (defaultPeriod === 'month') setMonthValue(defaultPeriodValue || thisMonth());
    if (defaultPeriod === 'fy') setFyValue(defaultPeriodValue || currentFY());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const periodValue = useMemo(() => {
    if (period === 'day') return dayValue;
    if (period === 'month') return monthValue;
    return fyValue;
  }, [period, dayValue, monthValue, fyValue]);

  const periodLabel = useMemo(() => {
    if (period === 'day' && dayValue) {
      try {
        return new Date(dayValue + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return dayValue; }
    }
    if (period === 'month' && monthValue) {
      try {
        return new Date(monthValue + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      } catch { return monthValue; }
    }
    if (period === 'fy') return `FY ${fyValue}`;
    return '';
  }, [period, dayValue, monthValue, fyValue]);

  const handleDownload = async () => {
    if (!periodValue) return;
    setDownloading(true);
    try {
      const params = { period, period_value: periodValue };
      if (customer?.customer_code) params.customer_code = customer.customer_code;
      if (division && division !== 'all') params.division = division;
      const res = await axios.get(`${API}/goods-returns/export/pdf`, {
        params, withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (customer?.name || customer?.customer_code || division || 'All').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40);
      link.setAttribute('download', `GoodsReturns_${safeName}_${periodValue}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Goods Returns PDF downloaded');
      onOpenChange(false);
    } catch (err) {
      let msg = err?.message || 'Unknown error';
      try {
        const blobText = await (err?.response?.data?.text?.() || Promise.resolve(''));
        if (blobText) {
          try { const parsed = JSON.parse(blobText); if (parsed?.detail) msg = parsed.detail; }
          catch { msg = blobText.slice(0, 300); }
        }
      } catch {}
      toast.error(`Export failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw size={16} className="text-indigo-600" />
            Goods Return Statement
          </DialogTitle>
          <DialogDescription>
            {customer?.name
              ? <span><span className="font-medium">{customer.name}</span> ({customer.customer_code})</span>
              : 'Pick a period and download the return credit-note statement as PDF.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Period</Label>
            <RadioGroup value={period} onValueChange={setPeriod} className="grid grid-cols-3 gap-2 mt-1">
              <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${period === 'day' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <RadioGroupItem value="day" /> Day
              </Label>
              <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${period === 'month' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <RadioGroupItem value="month" /> Month
              </Label>
              <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${period === 'fy' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <RadioGroupItem value="fy" /> FY
              </Label>
            </RadioGroup>
          </div>
          {period === 'day' && (
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={dayValue} onChange={(e) => setDayValue(e.target.value)} className="mt-1" />
            </div>
          )}
          {period === 'month' && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="mt-1" />
            </div>
          )}
          {period === 'fy' && (
            <div>
              <Label className="text-xs">Financial Year</Label>
              <Select value={fyValue} onValueChange={setFyValue}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyOptions(6).map(fy => <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Preview: <span className="font-medium">{customer?.name || customer?.customer_code || (division && division !== 'all' ? division : 'All customers')}</span> · {periodLabel}
            {division && division !== 'all' && customer?.name && <> · {division}</>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>Cancel</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={!periodValue || downloading}
            onClick={handleDownload}
          >
            <Download size={14} className="mr-1" />
            {downloading ? 'Building...' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
